
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../i18n/LanguageContext';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const FactoryContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) { font-size: 3rem; }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 80px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

const FactoryInfoSection = styled.section`
  margin-bottom: 120px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;
  text-align: center;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const InfoContent = styled.div`
  h2 {
    font-size: 2.5rem;
    
    color: #2c2c2c;
    margin-bottom: 30px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #374151;
    margin-bottom: 25px;
    
  }
  
  .highlight {
    background: linear-gradient(135deg, #2B4B8C, #3B82C4);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 500;
  }
`;

const FacilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 60px;
  text-align: left;
`;

const FacilityCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 1.5rem;
    color: #2B4B8C;
    margin-bottom: 15px;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: 10px;
    color: #6c757d;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.05rem;
    
    &::before {
      content: '✓';
      color: #c9a96e;
      font-weight: bold;
    }
  }
`;

const ContactSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 80px 0;
  border-radius: 30px;
  text-align: center;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
`;

const ContactContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h2 {
    font-size: 2.5rem;
    
    color: #2c2c2c;
    margin-bottom: 30px;
  }
  
  .contact-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 40px;
    margin-top: 50px;
  }
  
  .contact-item {
    background: white;
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease;
    
    &:hover { transform: translateY(-5px); }
    
    .icon {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #2B4B8C, #3B82C4);
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      color: white;
      margin: 0 auto 20px;
    }
    
    h3 {
      font-size: 1.3rem;
      color: #2c2c2c;
      margin-bottom: 10px;
      font-weight: 500;
    }
    
    p { margin: 0; font-size: 1rem; color: #6c757d; }
  }
`;

const Lojistik = () => {
  const { t } = useLanguage();
  const [animatedSections, setAnimatedSections] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <FactoryContainer>
      <Container>
        <PageTitle>{t('f.lojistikT')}</PageTitle>
        <PageSubtitle>{t('f.lojistikS')}</PageSubtitle>

        <FactoryInfoSection 
          id="info-section" 
          data-animate
          className={animatedSections.has('info-section') ? 'animate-in' : ''}
        >
          <InfoContent>
            <h2>{t("f.lojistikA")}</h2>
            <p>{t("f.lojistikDesc")}</p>
          </InfoContent>

          <FacilitiesGrid>
            <FacilityCard>
              <h3>{t("f.lojistikF")}</h3>
              <ul>
                <li>{t("f.lojistikF1")}</li>
                <li>{t("f.lojistikF2")}</li>
                <li>{t("f.lojistikF3")}</li>
                <li>{t("f.lojistikF4")}</li>
              </ul>
            </FacilityCard>
          </FacilitiesGrid>
        </FactoryInfoSection>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <ContactContent>
            <h2>{t("factory.contactTitle")}</h2>
            <div className="contact-info">
              <div className="contact-item">
                <div className="icon">📞</div>
                <h3>{t("factory.phone")}</h3>
                <p><bdi>0532 131 63 30</bdi><br/>{t("factory.facilityMgmt")}</p>
              </div>
              <div className="contact-item">
                <div className="icon">✉️</div>
                <h3>{t("factory.email")}</h3>
                <p>info@dmrcimento.com<br/>{t("factory.customerService")}</p>
              </div>
            </div>
          </ContactContent>
        </ContactSection>
      </Container>
    </FactoryContainer>
  );
};

export default Lojistik;
